import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Users,
  Users2,
  MapPin,
  Phone,
  Mail,
  Star,
  TrendingUp,
  DollarSign,
  Heart,
  RefreshCw,
  Package
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import CustomerService from '../../services/customerService';
import CustomerForm from './CustomerForm';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showViewForm, setShowViewForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    customerType: '',
    customerTier: '',
    status: '',
    industry: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 10
  });

  const { toasts, removeToast, success, error: showError } = useToast();

  // Load customers
  const loadCustomers = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        customerType: filters.customerType || undefined,
        customerTier: filters.customerTier || undefined,
        status: filters.status || undefined,
        industry: filters.industry || undefined
      };

      const response = await CustomerService.getCustomers(params);
      setCustomers(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError(error.message);
      showError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Load customer statistics
  const loadCustomerStats = async () => {
    try {
      const response = await CustomerService.getCustomerStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading customer stats:', error);
    }
  };

  useEffect(() => {
    loadCustomers();
    loadCustomerStats();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== undefined) {
        loadCustomers(1);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, filters]);

  // Handle customer actions
  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setShowForm(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowForm(true);
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowViewForm(true);
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteModal(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      await CustomerService.deleteCustomer(customerToDelete._id);
      success('Customer deleted successfully');
      loadCustomers(pagination.current);
      loadCustomerStats();
      setShowDeleteModal(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
      showError('Failed to delete customer');
    }
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      if (selectedCustomer) {
        await CustomerService.updateCustomer(selectedCustomer._id, customerData);
        success('Customer updated successfully');
      } else {
        await CustomerService.createCustomer(customerData);
        success('Customer created successfully');
      }
      setShowForm(false);
      setSelectedCustomer(null);
      loadCustomers(pagination.current);
      loadCustomerStats();
    } catch (error) {
      console.error('Error saving customer:', error);
      showError('Failed to save customer');
      throw error;
    }
  };

  const handleExportCustomers = async () => {
    try {
      const response = await CustomerService.exportCustomers('csv');
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      success('Customers exported successfully');
    } catch (error) {
      console.error('Error exporting customers:', error);
      showError('Failed to export customers');
    }
  };

  // Define columns for DataTable - Optimized for better layout
  const columns = [
    {
      key: 'customerId',
      label: 'ID',
      width: '120px',
      render: (value, row) => (
        <div className="p-2" style={{ 
          backgroundColor: '#f8f9fa', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="d-flex align-items-center gap-2">
            <Users2 size={14} style={{ color: '#4678be' }} />
            <span style={{ 
              fontSize: '12px', 
              fontWeight: '600',
              color: '#4678be',
              fontFamily: 'monospace'
            }}>
              {value || row._id?.slice(-8)}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Customer Name',
      width: '220px',
      render: (value, row) => (
        <div className="p-2" style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Users2 size={16} style={{ color: '#4678be' }} />
            <span className="fw-bold" style={{ fontSize: '15px', color: '#212529' }}>
              {value}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '2px' }}>
            {row.customerType} • {row.industry || 'No industry'}
          </div>
          {row.customerTier && (
            <div style={{ 
              fontSize: '10px', 
              fontWeight: '600', 
              color: '#4678be',
              backgroundColor: '#e3f2fd',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block'
            }}>
              {row.customerTier} Tier
            </div>
          )}
        </div>
      )
    },
    {
      key: 'customerType',
      label: 'Type',
      width: '120px',
      render: (value) => (
        <span style={{ fontSize: '13px' }}>{value}</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (value) => {
        const statusConfig = {
          'Active': { color: '#059669', bgColor: '#ecfdf5', icon: '✓' },
          'Inactive': { color: '#6b7280', bgColor: '#f9fafb', icon: '○' },
          'Suspended': { color: '#d97706', bgColor: '#fffbeb', icon: '⚠' },
          'Prospect': { color: '#3b82f6', bgColor: '#eff6ff', icon: '👁' },
          'Lead': { color: '#8b5cf6', bgColor: '#f3f0ff', icon: '🎯' },
          'Converted': { color: '#059669', bgColor: '#ecfdf5', icon: '🎉' }
        };
        
        const config = statusConfig[value] || statusConfig['Inactive'];
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: config.bgColor, 
            borderRadius: '8px',
            border: `1px solid ${config.color}20`
          }}>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '14px' }}>{config.icon}</span>
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '600',
                color: config.color
              }}>
                {value}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'primaryContact',
      label: 'Contact',
      width: '180px',
      render: (value) => {
        if (!value || !value.name) {
          return (
            <div className="d-flex align-items-center gap-2 p-2" style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <Users size={14} style={{ color: '#6c757d' }} />
              <span style={{ fontSize: '13px', color: '#6c757d' }}>
                No contact info
              </span>
            </div>
          );
        }
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <Users size={14} style={{ color: '#4678be' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#212529' }}>
                {value.name}
              </span>
            </div>
            {value.title && (
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '2px' }}>
                {value.title}
              </div>
            )}
            {value.email && (
              <div className="d-flex align-items-center gap-1">
                <Mail size={10} style={{ color: '#6c757d' }} />
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  {value.email}
                </span>
              </div>
            )}
            {value.phone && (
              <div className="d-flex align-items-center gap-1 mt-1">
                <Phone size={10} style={{ color: '#6c757d' }} />
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  {value.phone}
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'customerValue',
      label: 'Value',
      width: '160px',
      render: (value) => {
        const lifetimeValue = value?.lifetimeValue || 0;
        const averageOrderValue = value?.averageOrderValue || 0;
        
        // Determine value tier based on lifetime value
        const getValueTier = (value) => {
          if (value >= 1000000) return { tier: 'Enterprise', color: '#8b5cf6', bgColor: '#f3f0ff' };
          if (value >= 500000) return { tier: 'Premium', color: '#059669', bgColor: '#ecfdf5' };
          if (value >= 100000) return { tier: 'Gold', color: '#d97706', bgColor: '#fffbeb' };
          if (value >= 50000) return { tier: 'Silver', color: '#6b7280', bgColor: '#f9fafb' };
          return { tier: 'Bronze', color: '#dc2626', bgColor: '#fef2f2' };
        };
        
        const valueTier = getValueTier(lifetimeValue);
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: valueTier.bgColor, 
            borderRadius: '8px',
            border: `1px solid ${valueTier.color}20`
          }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <DollarSign size={14} style={{ color: valueTier.color }} />
              <span style={{ fontSize: '14px', fontWeight: '700', color: valueTier.color }}>
                ${lifetimeValue.toLocaleString()}
              </span>
            </div>
            <div style={{ 
              fontSize: '10px', 
              fontWeight: '600', 
              color: valueTier.color,
              backgroundColor: valueTier.color + '20',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block'
            }}>
              {valueTier.tier}
            </div>
            {averageOrderValue > 0 && (
              <div className="mt-1" style={{ fontSize: '11px', color: '#6c757d' }}>
                Avg: ${averageOrderValue.toLocaleString()}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  // Show view form instead of main content when viewing customer details
  if (showViewForm && selectedCustomer) {
    return (
      <div className="page-container">
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        
        {/* View Form Header */}
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="page-title d-flex align-items-center gap-2">
                <Eye size={24} style={{ color: '#4678be' }} />
                View Customer Details
              </h1>
              <p className="page-subtitle">
                {selectedCustomer.name} - Read Only View
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => {
                  setShowViewForm(false);
                  setSelectedCustomer(null);
                }}
              >
                <Eye size={16} />
                Back to List
              </button>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => {
                  setShowViewForm(false);
                  setSelectedCustomer(selectedCustomer);
                  setShowForm(true);
                }}
              >
                <Edit size={16} />
                Edit Customer
              </button>
            </div>
          </div>
        </div>

        {/* View Form Content */}
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body p-4">
                    <CustomerForm
                      customer={selectedCustomer}
                      viewMode={true}
                      onCancel={() => {
                        setShowViewForm(false);
                        setSelectedCustomer(null);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Header */}
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h1 className="page-title">
              Customer Management
            </h1>
            <p className="page-subtitle">
              Manage your customers and client relationships
            </p>
          </div>
          <div className="d-flex gap-2">
            <HelpButton />
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={handleExportCustomers}
            >
              <Download size={16} />
              Export
            </button>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleAddCustomer}
            >
              <Plus size={16} />
              Add Customer
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="page-stats-grid">
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-1">
                      {stats.overview.totalCustomers || 0}
                    </h3>
                    <p className="page-kpi-label">
                      Total Customers
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-1">
                    <Users2 size={24} className="page-kpi-icon color-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-2">
                      {stats.overview.activeCustomers || 0}
                    </h3>
                    <p className="page-kpi-label">
                      Active Customers
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-2">
                    <TrendingUp size={24} className="page-kpi-icon color-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-3">
                      ${stats.overview.totalLifetimeValue?.toLocaleString() || '0'}
                    </h3>
                    <p className="page-kpi-label">
                      Total Value
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-3">
                    <DollarSign size={24} className="page-kpi-icon color-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-4">
                      {stats.satisfaction.avgOverallRating?.toFixed(1) || '0.0'}
                    </h3>
                    <p className="page-kpi-label">
                      Avg Satisfaction
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-4">
                    <Heart size={24} className="page-kpi-icon color-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Data Table */}
      <div className="page-tab-content">
        <div className="page-table-container">
          <DataTable
            data={customers}
            columns={columns}
            title="Customer Directory"
            searchable={true}
            sortable={true}
            exportable={true}
            pagination={true}
            pageSize={pagination.limit}
            showActions={true}
            showCheckboxes={false}
            onView={handleViewCustomer}
            onEdit={handleEditCustomer}
            onDelete={handleDeleteCustomer}
            searchPlaceholder="Search customers by name, code, or type..."
            loading={loading}
            emptyMessage="No customers found. Click 'Add Customer' to get started."
          />
        </div>
      </div>

      {/* Customer Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '800px', maxWidth: '90vw' }}>
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <Users size={20} />
                {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  setSelectedCustomer(null);
                }}
                style={{ 
                  fontSize: '18px',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <CustomerForm
                customer={selectedCustomer}
                onSave={handleSaveCustomer}
                onCancel={() => {
                  setShowForm(false);
                  setSelectedCustomer(null);
                }}
              />
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Customer"
        message={`Are you sure you want to delete "${customerToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDeleteCustomer}
        onCancel={() => {
          setShowDeleteModal(false);
          setCustomerToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default CustomerManagement;
